import { GenericEventsRouteProvider } from '@wix/data-tools-generic-events';
import { useTranslation } from '../../../../lib/i18n';
import { ConversationsState } from '../../../../lib/conversations';
import { Me } from '../../../../lib/me';
import { ScrollArea } from '../../ui';
import { CreditsCard } from '../components/AccountCard';
import type { NavId } from '../hooks/use-sidebar-nav';
import { RailAccount, RailCreditsRing } from './RailAccount';
import { RailBell } from './RailBell';
import { RailChatsHeader, RailPinnedChats } from './RailChats';
import { RailCommunity } from './RailCommunity';
import { RailNav } from './RailNav';
import { RailSpaces } from './RailSpaces';
import { RailTooltip } from './RailTooltip';
import { RailWhatsapp } from './RailWhatsapp';
import styles from './RailSidebar.module.css';

/**
 * The NEW desktop rail, behind `symphonyNewSidebarWeb` (lib/new-sidebar-flag.ts); WideSidebar is the rail
 * everyone else gets. Figma `Symphony-Product`: open 22012:377914, closed 22012:417341, component 5272:26931.
 *
 * A white card floating 16px off the page's top, bottom and start edges, 280 wide open and 62 closed.
 * AppFrame publishes the card plus that gutter as `--sidebar-w` (296 / 78). The brand mark, the bell and the
 * collapse toggle of the open header are BrandHeader's, floated over the row this card reserves, because the
 * mark also owns the dev-tools long press.
 *
 * Open and closed are two LAYOUTS, not one clipped narrower: the strip carries glyphs the open rail draws
 * elsewhere (the bell, Search and New Chat), and its foot is four glyphs instead of three cards.
 */
export function RailSidebar({
  state,
  selectedId,
  navCurrent,
  me,
  collapsed = false,
  onExpand,
  onOpenSearch,
}: {
  state: ConversationsState;
  selectedId: string | null;
  navCurrent?: NavId;
  me: Me | null;
  collapsed?: boolean;
  // Expand the closed rail. A click on the strip anywhere that ISN'T a control expands it.
  onExpand?: () => void;
  onOpenSearch?: () => void;
}) {
  const handleRailClick = (e: React.MouseEvent) => {
    if (!collapsed || !onExpand) return;
    if ((e.target as HTMLElement).closest('a,button,input,select,textarea,[role="button"],[role="menu"]')) return;
    onExpand();
  };

  return (
    <aside
      className={`orion-sidebar orion-sidebar--wide ${styles.rail}`}
      data-open="true"
      data-collapsed={collapsed ? 'true' : undefined}
      onClick={handleRailClick}
    >
      <div className={collapsed ? `${styles.card} ${styles.cardCollapsed}` : styles.card}>
        {/* The header's row — BrandHeader draws into it. */}
        <div aria-hidden className={styles.headerSpace} />
        {collapsed ? (
          <CollapsedRail state={state} selectedId={selectedId} navCurrent={navCurrent} me={me} onExpand={onExpand} onOpenSearch={onOpenSearch} />
        ) : (
          <>
            {/* One scroll region for the chats row, the nav, the Spaces and the pins, so a long list never
                pushes the foot out of reach. */}
            <ScrollArea bar="hidden" className={styles.middle}>
              <GenericEventsRouteProvider value="conversations">
                <RailChatsHeader onOpenSearch={onOpenSearch} />
              </GenericEventsRouteProvider>
              <Divider />
              <RailNav current={navCurrent} />
              <Divider />
              <GenericEventsRouteProvider value="workspaces">
                <RailSpaces />
              </GenericEventsRouteProvider>
              <Divider />
              <GenericEventsRouteProvider value="conversations">
                <RailPinnedChats state={state} selectedId={selectedId} />
              </GenericEventsRouteProvider>
            </ScrollArea>
            <div className={styles.foot}>
              <RailWhatsapp />
              <GenericEventsRouteProvider value="credits">
                <CreditsCard />
              </GenericEventsRouteProvider>
              <div>
                <GenericEventsRouteProvider value="community">
                  <RailCommunity />
                </GenericEventsRouteProvider>
                <Divider tall />
                <GenericEventsRouteProvider value="account">
                  <RailAccount me={me} />
                </GenericEventsRouteProvider>
              </div>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

function CollapsedRail({
  state,
  selectedId,
  navCurrent,
  me,
  onExpand,
  onOpenSearch,
}: {
  state: ConversationsState;
  selectedId: string | null;
  navCurrent?: NavId;
  me: Me | null;
  onExpand?: () => void;
  onOpenSearch?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <>
      <ScrollArea bar="hidden" className={styles.middle}>
        <div className={styles.strip}>
          <RailTooltip label={t('nav.notifications')}>
            <RailBell />
          </RailTooltip>
          <GenericEventsRouteProvider value="conversations">
            <RailChatsHeader collapsed onOpenSearch={onOpenSearch} />
          </GenericEventsRouteProvider>
          <RailNav current={navCurrent} collapsed />
          <GenericEventsRouteProvider value="workspaces">
            <RailSpaces collapsed />
          </GenericEventsRouteProvider>
          <Divider collapsed />
          <GenericEventsRouteProvider value="conversations">
            <RailPinnedChats state={state} selectedId={selectedId} collapsed onExpand={onExpand} />
          </GenericEventsRouteProvider>
        </div>
      </ScrollArea>
      <div className={`${styles.foot} ${styles.stripFoot}`}>
        <RailWhatsapp collapsed />
        <GenericEventsRouteProvider value="community">
          <RailCommunity collapsed />
        </GenericEventsRouteProvider>
        <GenericEventsRouteProvider value="credits">
          <RailCreditsRing onExpand={onExpand} />
        </GenericEventsRouteProvider>
        <GenericEventsRouteProvider value="account">
          <RailAccount me={me} collapsed />
        </GenericEventsRouteProvider>
      </div>
    </>
  );
}

// Figma "Divider": a 1px Separator/Light rule in a 24px band (32 above the account row).
function Divider({ collapsed = false, tall = false }: { collapsed?: boolean; tall?: boolean }) {
  const cls = [styles.divider, collapsed && styles.dividerCollapsed, tall && styles.dividerTall].filter(Boolean).join(' ');
  return (
    <div aria-hidden className={cls}>
      <span className={styles.rule} />
    </div>
  );
}
